import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from "./AuthProvider";
import { Button } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import AddUser from './AddUser';
import Modal from 'react-bootstrap/Modal';
import { ADMIN, GAME_MASTER, PLAYER } from './Roles';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { UserStatus } from './UserStatus';

const DeleteButton = ({ user, onDelete }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { t } = useTranslation();

    return (
        <>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
              {t('common.delete')}
          </Button>
          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title> {t('users.confirm-deletion-title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {(user.role === ADMIN || user.role === GAME_MASTER) && t('users.confirm-deletion-game-master')}
              {user.role === PLAYER && t('users.confirm-deletion-player')}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={() => onDelete(user.id) && showDeleteModal(false)}>
                {t('common.confirmation')}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
    );
};

const UsersList = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [creating, setCreating] = useState(false);
    const { t } = useTranslation();
    const loadUsers = useCallback(() => {
        setLoading(true);
        axios.get('/api/users', {
            headers: {
                Authorization: `Bearer ${auth.token}`,
            },
        })
        .then(response => {
            setUsers(response.data);
            setLoading(false);
        })
        .catch(error => {
            console.log(error);
            setError(error);
            setLoading(false);
            if (error.status === 401) {
                auth.logOut();
            }
        });
    }, [auth]);

    const onDelete = (id) => {
        setLoading(true);
        axios.delete(`/api/users/${id}`, {
            headers: {
                Authorization: `Bearer ${auth.token}`,
            },
        })
        .then(response => {
            setLoading(false);
            navigate(`/users`);
        })
        .catch(error => {
            console.log(error);
            setError(error);
            setLoading(false);
        });
    };

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

       if (loading) return <p>{t('common.loading')}</p>;
       if (error) return <p>{t('users.loading-error')}: {error.message}</p>;

       return (
           <>
           <Table striped bordered hover>
             <thead>
               <tr>
                 <th>{t('users.username')}</th>
                 <th>{t('users.role')}</th>
                 <th>{t('users.status')}</th>
                 <th>{t('users.created-at')}</th>
                 <th/>
               </tr>
             </thead>
             <tbody>
               {users.map(user => {
                   return <tr key={user.id}>
                     <td>{user.username}</td>
                     <td>{t(`user.role.${user.role.toUpperCase()}`)}</td>
                     <td><UserStatus status={user.status} /></td>
                     <td>{user.createdAt}</td>
                     <td>{auth.roles.includes(ADMIN) && user.username !== 'admin' && <DeleteButton user={user} onDelete={onDelete} />}</td>
                   </tr>
               })}
             </tbody>
           </Table>
           <div>
               <Button variant="primary" onClick={() => setCreating(true)} disabled={creating}>
                 {t('common.add')}
               </Button>
               {creating && <AddUser show={creating} onClose={() => setCreating(false)} setCreating={setCreating} onAdded={loadUsers}/>}
           </div>
           </>
       );
};

export default UsersList;